import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Button } from "@libertify/ui";
import { PlayScript } from "components/PlayScript/";
import {
  parseScriptBySlides,
  reconstructScriptWithSlide,
} from "pages/media/projects/components/slides/";
import { isRTL } from "helpers/utils/";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useSectionVoiceId } from "../../../hooks/useSectionVoiceId";
import { useSlideMutations } from "../../../hooks/useSlideMutations";
import { useStoryboardEditing } from "../../../hooks/useStoryboardEditing";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { TabKeys, TemplateType } from "../../../types";
import { PanelSection } from "../PanelSection";
import { ScriptSectionsEditor } from "./ScriptSectionsEditor";
import { SlideScriptPanel } from "./SlideScriptPanel";
import { SpeakTransitionTitleToggle } from "./SpeakTransitionTitleToggle";
import { Narration } from "../../VoiceoverNarrationCard/components/Narration";

export const ScriptTab = () => {
  const t = useTranslate();
  const [isEditingWholeScript, setIsEditingWholeScript] = useState(false);

  const {
    projectId,
    organizationId,
    storyboardSections,
    templatesData: { storyboard, primaryDocumentId },
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      markMediaOutOfDate,
    },
  } = useTemplateData();

  const { updateStoryboardSection } = useStoryboardEditing({
    projectId: projectId ?? "",
    organizationId: organizationId ?? "",
    storyboardId: storyboard?.asset_id ?? "",
    primaryDocumentId: primaryDocumentId ?? "",
    markMediaOutOfDate,
  });

  const { removeSlide } = useSlideMutations();

  const {
    currentSlide,
    currentChapter,
    setCurrentChapter,
    templateState,
    updateParameteredText,
    slideNameToTemplateIdMap,
  } = useTemplateEditorStore((store) => ({
    currentSlide: store.currentSlide,
    currentChapter: store.currentChapter,
    setCurrentChapter: store.setCurrentChapter,
    templateState: store.templateState,
    updateParameteredText: store.updateParameteredText,
    slideNameToTemplateIdMap: store.slideNameToTemplateIdMap,
  }));

  const isTransitionScene =
    currentChapter.type === TemplateType.TRANSITION ||
    (currentSlide !== undefined &&
      mediaGenerationSettings?.chapters.some(
        (c) =>
          c.transition_template_id === slideNameToTemplateIdMap[currentSlide]
      ));

  // The tab pane stays mounted when switching tabs, so reset to the scene
  // script whenever the Script tab is left — returning should never reopen the
  // whole-script editor. (Derived during render per the no-setState-in-effect
  // guideline.)
  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const isScriptActive = useTemplatesStatusStore(
    (state) => state.activeTab === TabKeys.script
  );
  const [wasScriptActive, setWasScriptActive] = useState(isScriptActive);
  if (isScriptActive !== wasScriptActive) {
    setWasScriptActive(isScriptActive);
    if (!isScriptActive && isEditingWholeScript) {
      setIsEditingWholeScript(false);
    }
  }

  const save = (sectionId: string, content: string) => {
    updateStoryboardSection(sectionId, { script: { content } }).catch(
      (error) => {
        console.error("Error updating section script", error);
      }
    );
  };

  const onSlideTitleEdit = (newTitle: string, sectionId: string) => {
    const title = templateState.parameteredTexts?.find(
      (parameteredText) => parameteredText.path === "title"
    );
    if (title) {
      updateParameteredText({ ...title, content: newTitle });
      updateStoryboardSection(sectionId, { topic: newTitle }).catch((error) => {
        console.error("Error updating transition title", error);
      });
      addTemplateUpdate();
    }
  };

  const onChapterTitleEdit = (newTopic: string, sectionId: string) => {
    setCurrentChapter({ ...currentChapter, topic: newTopic });
    updateStoryboardSection(sectionId, { topic: newTopic }).catch((error) => {
      console.error("Error updating transition title", error);
    });
  };

  // Resolve the script of the currently selected item: a slide's slice in the
  // slide-based (Flash) editor, or a section's full script in the chapter-based
  // editor. Returns null for selections without a script (e.g. a transition).
  const selected = (() => {
    if (currentSlide) {
      const section = storyboardSections.find((s) =>
        s.slides.some((sl) => sl.uuid === currentSlide)
      );
      const transitionSlide = section?.slides.find(
        (sl) => sl.uuid === currentSlide
      );

      if (isTransitionScene && transitionSlide && section) {
        const slideTitle =
          "title" in transitionSlide.placeholders
            ? transitionSlide.placeholders.title
            : "";

        return {
          key: `${section.id}:${slideTitle}`,
          type: TemplateType.TRANSITION,
          content: slideTitle,
          sectionId: section.id,
          onSave: (newTitle: string) => onSlideTitleEdit(newTitle, section.id),
        };
      }

      const content = section
        ? parseScriptBySlides(section.script.content).get(currentSlide)
        : undefined;
      if (section && content) {
        return {
          key: currentSlide,
          content,
          sectionId: section.id,
          slideId: currentSlide,
          onSave: (edited: string) =>
            save(
              section.id,
              reconstructScriptWithSlide(
                section.script.content,
                currentSlide,
                edited
              )
            ),
        };
      }
      return null;
    }

    const section = currentChapter.sectionId
      ? storyboardSections.find((s) => s.id === currentChapter.sectionId)
      : undefined;
    if (!section) return null;

    if (isTransitionScene) {
      return {
        key: section.id,
        type: TemplateType.TRANSITION,
        content: section.topic,
        sectionId: section.id,
        onSave: (newTopic: string) => {
          onChapterTitleEdit(newTopic, section.id);
        },
      };
    }

    if (section.script.content) {
      return {
        key: section.id,
        content: section.script.content,
        sectionId: section.id,
        onSave: (edited: string) => save(section.id, edited),
      };
    }
    return null;
  })();

  const sectionVoiceId = useSectionVoiceId(selected?.sectionId);

  const sceneScriptPanel = selected ? (
    <Flex vertical gap={12}>
      {isTransitionScene ? (
        <TransitionTitlePanel
          key={`${selected.sectionId}:${selected.content}`}
          initialTopic={selected.content}
          isRTL={isRTL(storyboard?.language ?? "")}
          onSave={selected.onSave}
        />
      ) : (
        <SlideScriptPanel
          key={selected.key}
          initialScript={selected.content}
          isRTL={isRTL(storyboard?.language ?? "")}
          language={storyboard?.language}
          onSave={selected.onSave}
          sectionId={selected.sectionId}
          voiceId={sectionVoiceId}
          onDelete={() => {
            if (selected.slideId)
              removeSlide(selected.slideId, { discardScript: true }).catch(
                (error) => {
                  console.error("Error deleting slide", error);
                }
              );
          }}
        />
      )}
      <Flex justify="flex-start">
        <PlayScript
          text={selected.content}
          size="small"
          sectionId={"sectionId" in selected ? selected.sectionId : undefined}
          slideId={"slideId" in selected ? selected.slideId : undefined}
          voiceId={sectionVoiceId}
        />
      </Flex>
    </Flex>
  ) : (
    <Typography.Text type="secondary">
      {t("components.templates.settings.scriptTab.noScriptSelected")}
    </Typography.Text>
  );

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.scriptTab.title")}
        subtitle={t("components.templates.settings.scriptTab.description")}
        primary
      >
        <Flex justify="flex-start">
          <Button
            variant="outlined"
            icon={<EditOutlined />}
            onClick={() => setIsEditingWholeScript((prev) => !prev)}
          >
            {t(
              isEditingWholeScript
                ? "components.templates.settings.scriptTab.backToSceneScript"
                : "components.templates.settings.scriptTab.editWholeScript"
            )}
          </Button>
        </Flex>
      </PanelSection>
      <PanelSection>
        {isEditingWholeScript ? <ScriptSectionsEditor /> : sceneScriptPanel}
      </PanelSection>
    </>
  );
};

const TransitionTitlePanel = ({
  initialTopic,
  isRTL: rtl,
  onSave,
}: {
  initialTopic: string;
  isRTL: boolean;
  onSave: (topic: string) => void;
}) => {
  const [topic, setTopic] = useState(initialTopic);

  return (
    <Flex vertical gap={12}>
      <SpeakTransitionTitleToggle />
      <Narration
        script={topic}
        isEditable
        index={0}
        isRTL={rtl}
        onEditComplete={(_idx, updatedScript) => {
          const content =
            typeof updatedScript.content === "string" &&
            updatedScript.content.trim() !== ""
              ? updatedScript.content
              : topic;
          setTopic(content);
          onSave(content);
        }}
      />
    </Flex>
  );
};
